import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import SearchableSelect from "../SearchableSelect";
import { tableRows, bold } from "../../utils/xlsx";
import { download, ExportButton, fileName } from "./excel";
import { printHtml, tableHtml } from "../../utils/printTable";

const money = (value) => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const right = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

const columnsFor = (kind) => [
  { key: "skuCode", label: "Product Code" },
  { key: "productName", label: "Product Name" },
  { key: "openingStock", label: "Opening Stock", format: "qty", total: false },
  { key: "quantity", label: kind === "in" ? "Stock In Qty" : "Stock Out Qty", format: "qty" },
  { key: "closingStock", label: "Closing Stock", format: "qty", total: false },
  { key: "rate", label: "Rate / Item", format: "money", total: false },
  { key: "mrp", label: "MRP", format: "money", total: false },
  { key: "value", label: kind === "in" ? "Value of Stock In" : "Value of Stock Out", format: "money" },
];

const JournalTable = ({ title, kind, rows, totalQuantity, totalValue }) => (
  <div style={{ minWidth: 0 }}>
    <h4 style={{ fontWeight: 700, margin: "4px 0 6px" }}>{title}</h4>
    <div className="table-wrapper" style={{ marginTop: 0, maxHeight: "65vh", overflowY: "auto" }}>
      <table>
        <thead>
          <tr>
            {columnsFor(kind).map((c) => (
              <th key={c.key} style={c.format ? right : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8}>
                <div className="empty-state">
                  <span className="empty-state-title">No {kind === "in" ? "stock in" : "stock out"} in this period</span>
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.productId}>
                <td>{row.skuCode}</td>
                <td>{row.productName}</td>
                <td style={right}>{qty(row.openingStock)}</td>
                <td style={{ ...right, fontWeight: 600 }}>{qty(row.quantity)}</td>
                <td style={right}>{qty(row.closingStock)}</td>
                <td style={right}>{money(row.rate)}</td>
                <td style={right}>{row.mrp === null || row.mrp === undefined ? "-" : money(row.mrp)}</td>
                <td style={right}>{money(row.value)}</td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ fontWeight: 700 }}>
              Grand Total
            </td>
            <td style={{ ...right, fontWeight: 700 }}>{qty(totalQuantity)}</td>
            <td colSpan={3} />
            <td style={{ ...right, fontWeight: 700 }}>{money(totalValue)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

/**
 * Stock journal: per product, the stock that came in and went out over a period (purchases, returns, stock in / out, transfers,
 * deliveries, adjustments), each with its opening and closing stock, rate, MRP and value, and grand totals — stock in and stock
 * out side by side.
 */
const StockJournalReport = () => {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [productId, setProductId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/api/v1/products", { size: 2000 }).then((d) => setProducts(d?.content || [])).catch(() => {});
    api.get("/api/v1/warehouses/all").then((d) => setWarehouses(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/inventory/reports/stock-journal", {
        productId: productId || undefined,
        warehouseId: warehouseId || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setJournal(data);
    } catch (error) {
      toast.error(error.message || "Failed to load the stock journal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const describe = () => {
    const parts = [`${journal.fromDate} to ${journal.toDate}`];
    if (productId) parts.push(`Product: ${products.find((p) => String(p.id) === String(productId))?.name || productId}`);
    if (warehouseId) parts.push(`Warehouse: ${warehouses.find((w) => String(w.id) === String(warehouseId))?.name || warehouseId}`);
    return parts.join(" — ");
  };

  const exportExcel = () =>
    download(fileName("Stock Journal", `${journal.fromDate} to ${journal.toDate}`), [
      { name: "Stock In", rows: [[bold("Stock Journal — Stock In")], [describe()], [], ...tableRows(columnsFor("in"), journal.stockIn, { totals: true })] },
      { name: "Stock Out", rows: [[bold("Stock Journal — Stock Out")], [describe()], [], ...tableRows(columnsFor("out"), journal.stockOut, { totals: true })] },
    ]);

  const printPdf = () =>
    printHtml(
      "Stock Journal",
      describe(),
      `<h2>Stock In</h2>${tableHtml(columnsFor("in"), journal.stockIn, { totals: true, totalLabel: "Grand Total" })}
       <h2>Stock Out</h2>${tableHtml(columnsFor("out"), journal.stockOut, { totals: true, totalLabel: "Grand Total" })}`
    );

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading stock journal...</div>
          </div>
        </div>
      ) : null}
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Stock movements per product over the period (this month by default): everything that came in — purchases, returns, stock in,
        transfers in — and everything that went out — deliveries, purchase returns, stock out, transfers out. Rate is the purchase price
        for stock in and the selling price for stock out.
      </div>
      <div className="filters-bar">
        <div style={{ minWidth: 240 }}>
          <SearchableSelect
            value={productId}
            onChange={setProductId}
            options={[{ value: "", label: "All products" }, ...products.map((p) => ({ value: p.id, label: p.name, sublabel: p.skuCode }))]}
            placeholder="All products"
          />
        </div>
        <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} aria-label="Warehouse">
          <option value="">All warehouses</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!journal} />
        <button className="secondary" onClick={printPdf} disabled={!journal} title="Print, or save as PDF from the print dialog">
          Print / PDF
        </button>
      </div>
      {journal ? (
        <>
          <div className="page-subtitle" style={{ marginTop: 8 }}>
            {journal.fromDate} to {journal.toDate}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(520px, 1fr))", gap: 16, marginTop: 8, alignItems: "start" }}>
            <JournalTable title="Stock In" kind="in" rows={journal.stockIn} totalQuantity={journal.totalInQuantity} totalValue={journal.totalInValue} />
            <JournalTable title="Stock Out" kind="out" rows={journal.stockOut} totalQuantity={journal.totalOutQuantity} totalValue={journal.totalOutValue} />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default StockJournalReport;
